import React, { useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { Alert, Box, Button, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ReasonModal, useReasonPrompt } from './ReasonPromptModal';
import MissingRequiredFieldsModal from '../../../components/general/MissingRequiredFieldsModal';
import { formatSubmissionDateTime } from '../../../utils/dateUtils';
import {
    decodeVisitStatusRefusal,
    hasRecentDeviceActivity,
    OtherOpenDraft,
    RECENT_DEVICE_ACTIVITY_DAYS,
    VisitMissingRequiredField,
    VisitStatusChange,
    visitStatusOffer,
} from './visitStatusChange';

/**
 * SKIN-543 — "Close visit" / "Reopen visit" in a visit's accordion header, with a
 * required reason; SKIN-696 — and "Void visit" on a DRAFT (DRAFT → VOIDED, one-way).
 * The parent renders it only for APPROVE_VISIT holders on studies with
 * FUNCTIONALITY.WEB_VISIT_STATUS, and owns the API call (`onChangeStatus`).
 *
 * A DRAFT offers two actions — Close and Void — so each is its own
 * `VisitStatusActionButton` with its own dialog state: they never share a reason
 * or an open flag.
 *
 * A close the API refuses for missing required fields opens SKIN-536's
 * MissingRequiredFieldsModal instead of an error line: the visit stays open and the
 * reviewer can jump to the submission to fill them in.
 *
 * Opening the reopen dialog asks `loadOtherOpenDrafts` whether the visit's author already
 * holds another open draft on the case. If so the dialog names it and asks to have the
 * author close (submit) it in the app first (two drafts of the same encounter otherwise); it
 * warns, it does not block. Confirm waits until the check has answered or failed.
 */
interface VisitStatusActionProps {
    status?: string;
    /** the newest submission of the visit; drives the "a phone may still hold data" warning */
    lastSubmissionAt?: Date | string | null;
    /** rejects with the API's error when the change is refused */
    onChangeStatus: (change: VisitStatusChange, reason: string) => Promise<unknown>;
    onJumpToMissingField?: (field: VisitMissingRequiredField) => void;
    /** the author's other open drafts on the case (GET .../web/reopen-check); read when the reopen dialog opens */
    loadOtherOpenDrafts?: () => Promise<OtherOpenDraft[]>;
}

// Clicks inside an AccordionSummary toggle the accordion; the action must not.
const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * One web action (close, reopen or void) as a button plus its reason dialog. A DRAFT
 * renders two of these, so the dialog state (reason, open, the reopen check) is scoped
 * per action rather than shared.
 */
const VisitStatusActionButton: React.FC<{ action: VisitStatusChange } & VisitStatusActionProps> = ({
    action, lastSubmissionAt, onChangeStatus, onJumpToMissingField, loadOtherOpenDrafts,
}) => {
    const prompt = useReasonPrompt();
    const [missing, setMissing] = useState<VisitMissingRequiredField[] | null>(null);
    // 'checking': in flight (Confirm waits for it); 'failed': the check could not be made
    const [otherDrafts, setOtherDrafts] = useState<OtherOpenDraft[] | 'checking' | 'failed' | null>(null);
    // only the answer for the latest opening of the dialog may land
    const checkRun = useRef(0);

    const confirm = () => prompt.submit({
        requiredKey: 'hospital.visit-status-reason-required',
        action: async (reason) => {
            try {
                await onChangeStatus(action, reason);
            } catch (error) {
                const refusal = decodeVisitStatusRefusal(error);
                if (refusal.kind !== 'missing') throw error;
                // Not an error in the dialog: the visit stays open and the list says why.
                setMissing(refusal.missing);
            }
        },
        resolveError: (error) => {
            const refusal = decodeVisitStatusRefusal(error);
            return refusal.kind === 'raw' ? refusal.message : refusal.kind === 'message' ? refusal.translationId : '';
        },
    });

    const openDialog = () => {
        prompt.open(0);
        setOtherDrafts(null);
        if (action !== 'reopen' || !loadOtherOpenDrafts) return;
        setOtherDrafts('checking');
        const run = ++checkRun.current;
        loadOtherOpenDrafts().then(
            (drafts) => { if (run === checkRun.current) setOtherDrafts(drafts); },
            () => { if (run === checkRun.current) setOtherDrafts('failed'); },
        );
    };

    const warnDevice = action === 'close' && hasRecentDeviceActivity(lastSubmissionAt);
    const drafts = action === 'reopen' && Array.isArray(otherDrafts) ? otherDrafts : [];
    // Void is a one-way, destructive retire: a distinct (error-coloured) affordance.
    const color = action === 'void' ? 'error' : 'primary';

    return (
        <>
            {/* the prompt is index-based (built for table rows); there is one subject here */}
            <Button size="small" variant="outlined" color={color} onClick={openDialog}>
                <Translate id={`hospital.visit-status-${action}`} />
            </Button>
            <ReasonModal
                prompt={prompt}
                title={<Translate id={`hospital.visit-status-${action}-title`} />}
                body={
                    <>
                        <Translate id={`hospital.visit-status-${action}-body`} />
                        {warnDevice && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Translate id="hospital.visit-status-close-device-warning" data={{ days: RECENT_DEVICE_ACTIVITY_DAYS }} />
                            </Alert>
                        )}
                        {drafts.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Translate id="hospital.visit-status-reopen-other-draft-warning" />
                                <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
                                    {drafts.map((draft) => (
                                        <li key={draft.uuid}>
                                            <Translate
                                                id={draft.lastSubmissionAt
                                                    ? 'hospital.visit-status-reopen-other-draft-item'
                                                    : 'hospital.visit-status-reopen-other-draft-item-empty'}
                                                data={{
                                                    started: formatSubmissionDateTime(draft.createdAt),
                                                    last: draft.lastSubmissionAt ? formatSubmissionDateTime(draft.lastSubmissionAt) : '',
                                                }}
                                            />
                                        </li>
                                    ))}
                                </Box>
                                <Translate id="hospital.visit-status-reopen-other-draft-advice" />
                            </Alert>
                        )}
                        {action === 'reopen' && otherDrafts === 'failed' && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Translate id="hospital.visit-status-reopen-other-draft-check-failed" />
                            </Alert>
                        )}
                    </>
                }
                reasonLabel={<Translate id="hospital.visit-status-reason" />}
                confirmLabel={`hospital.visit-status-${action}-confirm`}
                onConfirm={confirm}
                // the reviewer decides with the warning in front of them, not before it arrives
                confirmDisabled={otherDrafts === 'checking'}
            />
            {action === 'close' && (
                <MissingRequiredFieldsModal
                    open={missing !== null}
                    missing={missing ?? []}
                    introId="hospital.visit-status-missing-intro"
                    backId="hospital.visit-status-missing-back"
                    onClose={() => setMissing(null)}
                    onJumpTo={(field) => {
                        setMissing(null);
                        onJumpToMissingField?.(field);
                    }}
                />
            )}
        </>
    );
};

const VisitStatusAction: React.FC<VisitStatusActionProps> = (props) => {
    const offer = visitStatusOffer(props.status);

    if (offer === null) return null;
    if (offer.kind === 'locked') {
        return (
            <Box display="flex" alignItems="center" gap={0.5} sx={{ ml: 'auto', color: 'text.secondary' }}>
                <LockOutlinedIcon fontSize="small" />
                <Typography variant="caption"><Translate id="hospital.visit-status-locked-hint" /></Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }} onClick={stop} onFocus={stop}>
            {offer.actions.map((action) => (
                <VisitStatusActionButton key={action} action={action} {...props} />
            ))}
        </Box>
    );
};

export default VisitStatusAction;
